// @ts-check
/**
 * PostHog engagement events — Renpho US theme global JS.
 * Loaded via snippets/td-scripts.liquid with `defer`. DOM is ready at execution.
 *
 * NOTE: B2 (add_to_cart fetch interceptor) lives inline in layout/theme.liquid
 * <head>, NOT here — it must wrap window.fetch before any add-to-cart button
 * is interactive, which `defer` cannot guarantee. This file only handles
 * B3/B4/B5 which all react to user gestures (click/scroll) that necessarily
 * happen after DOMContentLoaded.
 */

/**
 * @typedef {object} ShopifyLineItemResponse
 * @property {number} [product_id]
 */

/**
 * @typedef {object} ShopifyCartResponse
 * @property {number} [total_price]
 * @property {number} [item_count]
 * @property {ShopifyLineItemResponse[]} [items]
 * @property {string} [currency]
 */

(function () {
  if (window.__renphoEngagementBound) return;
  window.__renphoEngagementBound = true;

  /**
   * @param {string} eventName
   * @param {Record<string, unknown>} [props]
   */
  function safeCapture(eventName, props) {
    try {
      const ph = window.posthog;
      if (ph && typeof ph.capture === 'function') {
        ph.capture(eventName, props || {});
      }
    } catch (_e) {
      /* swallow — tracking must never break the page */
    }
  }

  /** @returns {string} */
  function shopifyCurrency() {
    const sh = window.Shopify;
    return (sh && sh.currency && sh.currency.active) || 'USD';
  }

  /**
   * @param {ShopifyCartResponse | null | undefined} cart
   * @returns {Record<string, unknown>}
   */
  function cartSummary(cart) {
    const items = (cart && cart.items) || [];
    return {
      cart_value:
        cart && typeof cart.total_price === 'number' ? cart.total_price / 100 : null,
      items: cart ? cart.item_count : 0,
      product_ids: items.map(function (i) {
        return i.product_id;
      }),
      currency: (cart && cart.currency) || shopifyCurrency(),
    };
  }

  /** @returns {Promise<ShopifyCartResponse | null>} */
  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  // ---------------------------------------------------------------------------
  // B3: cart_viewed — fire whenever the cart drawer dialog opens, or the user
  // lands on /cart. Renpho's <cart-drawer-component> wraps a <dialog> that
  // gains an `open` attribute when shown — watching that attribute catches
  // both manual trigger clicks and the auto-open after add-to-cart.
  // ---------------------------------------------------------------------------
  let cartViewedFiring = false;
  function captureCartViewed() {
    if (cartViewedFiring) return;
    cartViewedFiring = true;
    fetchCart().then(function (cart) {
      safeCapture('cart_viewed', cartSummary(cart));
      cartViewedFiring = false;
    });
  }

  if (
    window.location.pathname === '/cart' ||
    window.location.pathname.indexOf('/cart?') === 0
  ) {
    captureCartViewed();
  }

  function bindCartDrawerObserver() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return false;
    const dialog = drawer.querySelector('dialog');
    if (!dialog) return false;
    let wasOpen = dialog.hasAttribute('open');
    new MutationObserver(function () {
      const nowOpen = dialog.hasAttribute('open');
      if (nowOpen && !wasOpen) captureCartViewed();
      wasOpen = nowOpen;
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    return true;
  }

  if (!bindCartDrawerObserver()) {
    const bodyObserver = new MutationObserver(function () {
      if (bindCartDrawerObserver()) bodyObserver.disconnect();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Fallback click selector for non-drawer themes / cart-link headers.
  document.addEventListener(
    'click',
    function (e) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // Skip clicks inside cart-drawer-component — the dialog observer handles those.
      if (target.closest('cart-drawer-component')) return;
      const trigger = target.closest(
        'a[href="/cart"], a[href^="/cart?"], ' +
          '[data-cart-trigger], [data-action="open-cart"]'
      );
      if (trigger) captureCartViewed();
    },
    true
  );

  // ---------------------------------------------------------------------------
  // B4: checkout_started — replace fragile autocapture text-match with a named event.
  // ---------------------------------------------------------------------------
  document.addEventListener(
    'click',
    function (e) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest(
        'button[name="checkout"], input[name="checkout"], ' +
          'a[href^="/checkout"], a[href*="/checkouts/"], ' +
          '[data-checkout], [data-checkout-button]'
      );
      if (!trigger) return;
      fetchCart().then(function (cart) {
        safeCapture('checkout_started', cartSummary(cart));
      });
    },
    true
  );

  // ---------------------------------------------------------------------------
  // B5: scroll_depth — fire 25/50/75/100% once per page, throttled with rAF.
  // ---------------------------------------------------------------------------
  (function () {
    /** @type {number[]} */
    const thresholds = [25, 50, 75, 100];
    /** @type {Record<number, boolean>} */
    let fired = {};
    let ticking = false;

    /** @returns {string} */
    function templateName() {
      const cls = (document.body && document.body.getAttribute('class')) || '';
      const m = cls.match(/template-([a-z0-9_-]+)/i);
      return m && m[1] ? m[1] : 'unknown';
    }

    function measure() {
      ticking = false;
      const docEl = document.documentElement;
      const scrollTop = window.pageYOffset || docEl.scrollTop || 0;
      const viewport = window.innerHeight || docEl.clientHeight;
      const fullHeight = Math.max(
        docEl.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      );
      if (fullHeight <= 0) return;
      const pct = Math.round(((scrollTop + viewport) / fullHeight) * 100);
      thresholds.forEach(function (t) {
        if (pct >= t && !fired[t]) {
          fired[t] = true;
          safeCapture('scroll_depth', { percent: t, template: templateName() });
        }
      });
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(measure);
      },
      { passive: true }
    );

    // Reset on bfcache restore so the next view starts fresh.
    window.addEventListener('pageshow', function (ev) {
      if (ev.persisted) {
        fired = {};
        measure();
      }
    });
  })();
})();
